import { useParams } from "react-router-dom";
import { applyCampaign } from "../services/api/campaign";
import React, { useCallback, useEffect, useState } from "react";
import { ApplicationStatus } from "../types/sponsorships/campaigns";
import CampaignImageSlider from "../components/campaign/CampaignImageSlider";
import { arrayOfUniqueStrings, getUserMobileOS, sanitizeURLs, textToTitleCase } from "../utils/helpers";
import StrategyIcon from "../components/icons/StrategyIcon";
import DOMPurify from 'dompurify';
import { useSelector } from "react-redux";
import { AppDispatch, RootState, store } from "../redux-stores/store";
import { useDispatch } from "react-redux";
import { setShowFooterNav, toggleAddSocialAccountModal, toggleLocationUpdateModal, toggleProfileUpdateModal } from "../redux-stores/slices/layoutSlice";
import { toast } from "react-toastify";
import { useConfirmation } from "../contexts/ConfirmationContext";
import usePageTitle from "../hooks/usePageTitle";
import InfluencerActionCardModal from "../components/influencer/InfluencerActionCardModal";
import CampaignDetailSkeleton from "../skeletons/CampaignDetailSkeleton";
import { fetchApplication, fetchCampaign } from "../redux-stores/slices/campaignDetailsSlice";
import { markExpired as markCollaborationsExpired } from "../redux-stores/slices/collaborationSlice";
import { ERROR } from "../constants/errors";
import { POSTAPIError } from "../types";
import InsufficientPermissionsCTAModal from "../components/CTA/InsufficientPermissionsCTAModal";
import GenericErrorsCTAModal from "../components/CTA/GenericErrorsCTAModal";
import CampaignApplyConfirmationModal from "../components/campaign/CampaignApplyConfirmationModal";
import MilestoneMediaPNG from "../assets/images/milestone-media.png"
import { GENERAL_GIGTYPE_ID, platforms } from "../constants/constants";
import { getLocalStorageItem, setLocalStorageItem } from "../utils/localStorage";
import { useLocationAccess } from "../contexts/LocationContext";
import { Link } from "react-router-dom";
import { detectBrowser } from "../utils/detectBrowser";
import { debug } from "../services/api/auth";
import { SocialAccountData } from "../types/influencer/influencerTypes";
import instagramLogo from "../assets/images/platforms/platform-instagram.png"
import youtubeLogo from "../assets/images/platforms/platform-youtube.png"
import CampaignAccountSelectorModal from "../components/campaign/CampaignAccountSelectorModal";
import { analyticsLog } from "../lib/analytics";
import { isNativePlatform } from "../utils/platformUtil";
import OutOfCredits from "../components/campaign/OutOfCredits";
import { getNotificationPermissionStatus, showNotificationEnablePrompt } from "../lib/pushNotifications";

export const platformLogos = {
    ig: instagramLogo,
    yt: youtubeLogo
}

const Campaign: React.FC = () => {
    /* Page parameters */
    const { campaignId } = useParams();
    /* Page parameters */

    /* Local States */
    const [applicationStatus, setApplicationStatus] = useState<ApplicationStatus>({ status: 'NOT_APPLIED' })
    const [canApply, setCanApply] = useState<boolean>(false)
    const [connectionRequiredModalVisibility, setConnectionRequiredModalVisibility] = useState<boolean>(false);
    const [applicationError, setApplicationError] = useState<null | POSTAPIError>(null)
    const [confirmationBox, setConfirmationBox] = useState(false);
    /* Local States */

    /* Redux States */
    const campaign = useSelector((state: RootState) => state.campaignDetails.campaigns[campaignId!] ?? null);
    const campaignData = campaign ? campaign.campaignData : null;
    const applicationData = campaign ? campaign.applicationData : null
    // const profileCompletion = useSelector((state: RootState) => state.influencer.influencer?.profile_completion);
    const profileUpdateModalVisibility = useSelector((state: RootState) => state.layout.profile_update_modal_visibility)
    const locationUpdateModalVisibility = useSelector((state: RootState) => state.layout.location_update_modal_visibility)
    const addSocialAccountModalVisibility = useSelector((state: RootState) => state.layout.add_social_account_modal_visibility)
    const connectionRequired = useSelector((state: RootState) => state.influencer.connectionRequired);
    const languages = useSelector((state: RootState) => state.auth.master?.language_list) ?? {};
    const influencer = useSelector((state: RootState) => state.influencer?.influencer);
    const socialAccounts = useSelector((state: RootState) => state.influencer?.social_accounts);
    const [locationRequired, setLocationRequired] = useState<boolean>(false);
    const [socialAccount, setSocialAccount] = useState<SocialAccountData | null>(null);
    const [eligibleSocialAccounts, setEligibleSocialAccounts] = useState<SocialAccountData[]>([]);
    const [campaignPlatform, setCampaignPlatform] = useState<{id: string, name: string} | null>(null);
    const [campaignAccountSelectorVisible, setCampaignAccountSelectorVisible] = useState<boolean>(false);
    const profileCompletion = useSelector((state: RootState) => state.influencer.influencer?.profile_completion);
    /* Redux States */

    /* Hooks */
    const dispatch = useDispatch<AppDispatch>();
    // const confirmationBox = useConfirmation();
    usePageTitle(campaignData?.campaign?.name ?? '')
    // const { location, requestLocation, declined: locationDeclined, error: locationError, allowLocationAccess, isLocationServiceEnabled, isFetchingLocation } = useLocationAccess();
    /* Hooks */


    /* Data handling functions */
    const fetchCampaignData = useCallback(async () => {
        if (!campaignId) return;
        dispatch(fetchCampaign(campaignId));
    }, [campaignId, dispatch])

    const getCriteriaMatchComponent = (skip = false) => {
        if (!applicationData?.match || skip) return "Are you sure to proceed with the application?"
        return (
            <div>
                <div className="text-center">
                    <span className="fs-3 text-uppercase pop fw-bolder">{applicationData?.match?.requirement_match}%</span>
                    <span className="fs-5 ms-2">Requirements Matched!</span>
                </div>

                <div className="mt-2">
                    {
                        applicationData?.match?.requirement_matches &&
                        Object.values(applicationData.match.requirement_matches).map((requirement, i) => {
                            return (
                                <div key={i} className="d-flex justify-content-between align-items-center mt-1">
                                    <span>{requirement.name}</span>
                                    {
                                        requirement.match ? (
                                            <span><i className="fa-solid fa-circle-check text-success fs-5"></i></span>
                                        ) : (
                                            <span><i className="fa-solid fa-circle-xmark text-danger fs-5"></i></span>
                                        )
                                    }
                                </div>
                            )
                        })
                    }
                </div>
                <div className="mt-2 text-danger">
                    Disclaimer: Application selection is decided by brand.
                </div>
            </div>
        )
    }

    const showConfirmationBox = () => {
        if (!campaignData?.campaign) return;
        if (!influencer?.name || !influencer?.age || !influencer.gender || !influencer.location) { // instead of profileCompletion !== 100
            setConfirmationBox(false);
            return dispatch(toggleProfileUpdateModal(true));
        }
        // if(locationRequired && !influencer?.location) {
        //     setConfirmationBox(false);
        //     return dispatch(toggleLocationUpdateModal(true));
        // } 
        
        if(!socialAccount && eligibleSocialAccounts.length > 1){
            return setCampaignAccountSelectorVisible(true);
        }

        if (!socialAccount)
            return dispatch(toggleAddSocialAccountModal({active: true, ...(campaignPlatform ? {platforms: [campaignPlatform]} : {})}));

        if (socialAccount && campaign?.campaignData?.campaign?.platform === 'ig' && campaign?.campaignData?.campaign?.connection_required && socialAccount?.status !== 'connected')
            return setConnectionRequiredModalVisibility(true);

        setConfirmationBox(true);
        return;
    }

    // Function to fetch user location, returns {latitude, longitude} or false/null if denied
    const fetchUserLocation = (): Promise<{ lat: number; lng: number } | false | null> => {
        return new Promise((resolve) => {
            if (!navigator.geolocation) {
                resolve(null);
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    if (error.code === error.PERMISSION_DENIED) {
                        resolve(false);
                    } else {
                        resolve(null);
                    }
                }
            );
        });
    };

    // useEffect(() => {
    //     requestLocation?.();
    // }, [requestLocation])


    const apply = async (languageId?: string) => {
        if (!campaignData?.campaign || !campaignId) return;

        
        if (!influencer?.name || !influencer?.age || !influencer.gender || !influencer.location) { // instead of profileCompletion !== 100
            setConfirmationBox(false);
            return dispatch(toggleProfileUpdateModal(true));
        }


        // if(locationRequired && !influencer?.location) {
        //     setConfirmationBox(false);
        //     return dispatch(toggleLocationUpdateModal(true));
        // } 
        if (!socialAccount)
            return dispatch(toggleAddSocialAccountModal({active: true, ...(campaignPlatform ? {platforms: [campaignPlatform]} : {})}));

        try {

            // if (!location && locationRequired) {
            //     await debug({ location, locationDeclined, isLocationServiceEnabled, locationError, mobileOS: getUserMobileOS(), browser: detectBrowser() })
            //     toast.error("Location access is required to apply!");
            //     return false;
            // }

            // const locationCoords = location ? { lat: location.latitude, lng: location.longitude } : {};
            const locationCoords = {};

            setApplicationStatus({ status: 'PENDING' });
            const campaignReferrals = getLocalStorageItem('campaign_referrals');
            let referrer;
            if (campaignReferrals && campaignReferrals[campaignId]) {
                referrer = campaignReferrals[campaignId];
            }
            await applyCampaign(campaignData?.campaign.uuid, socialAccount?.social_account?.uuid, locationCoords, languageId, referrer);
            analyticsLog('campaign_application_success', 
                {
                    campaign_id: campaignData?.campaign.uuid, 
                    social_platform: socialAccount?.social_account?.platform,
                    social_handle: socialAccount?.social_account?.handle, 
                    ...(languageId ? {language: languages[languageId]} : {}),
                    campaign_name: campaignData?.campaign?.name, 
                    brand_name: campaignData?.campaign?.brand?.name
                }
            )
            dispatch(fetchApplication(campaignId!))
            dispatch(markCollaborationsExpired())
            setConfirmationBox(false);
            toast.success("Applied Successfully!");
            delete campaignReferrals[campaignId];
            setLocalStorageItem('campaign_referrals', campaignReferrals);
        } catch (error: any) {
            analyticsLog('campaign_application_failed', 
                {
                    campaign_id: campaignData?.campaign.uuid, 
                    social_platform: socialAccount?.social_account?.platform,
                    social_handle: socialAccount?.social_account?.handle, 
                    ...(languageId ? {language: languages[languageId]} : {}),
                    campaign_name: campaignData?.campaign?.name, 
                    brand_name: campaignData?.campaign?.brand?.name,
                    error_code: (error?.response?.data as POSTAPIError)?.error_code,
                }
            )
            setApplicationError(error?.response?.data)
            setApplicationStatus({ status: 'FAILED' });
        } finally {
            setConfirmationBox(false);
        }

    }
    /* Data handling functions */

    /* Helper functions */
    const getCampaignImages = () => {
        let images: string[] = campaignData?.campaign.hero_img_url ? [campaignData.campaign.hero_img_url] : [];
        if (campaignData?.campaign.gallery_images)
            images = [...images, ...campaignData.campaign.gallery_images];

        return arrayOfUniqueStrings(images);
    }

    const shareURL = async () => {
        if (campaignData) {
            try {
                let share_url = campaignData?.share_url;
                if (store.getState().auth.user?.referral_code) {
                    share_url += '/?referrer=' + store.getState().auth.user?.referral_code;
                }
                await navigator.clipboard.writeText(share_url);
                toast.success('URL copied to your clipboard! 🙂');
            } catch (error) {
                console.error('Failed to copy text');
            }
        }
    }
    /* Helper functions */

    /* Lifecycle hooks */
    useEffect(() => {
        if (applicationData) {
            setCanApply(applicationData.can_apply ?? false);
            if (applicationData.application)
                setApplicationStatus({ status: 'APPLIED' });
        }
    }, [applicationData])

    useEffect(() => {
        setLocationRequired(applicationData?.require_location ?? false);
    }, [applicationData])

    useEffect(() => {
        fetchCampaignData();
    }, [campaignId, fetchCampaignData])

    useEffect(() => {
        if (profileUpdateModalVisibility && influencer?.name && influencer?.age && influencer.gender && influencer?.location)
            dispatch(toggleProfileUpdateModal(false));
    }, [influencer, profileUpdateModalVisibility])

     useEffect(() => {
        if (locationUpdateModalVisibility && influencer?.location)
            dispatch(toggleLocationUpdateModal(false));
    }, [influencer, locationUpdateModalVisibility])

    useEffect(() => {
        if (addSocialAccountModalVisibility?.active && socialAccount)
            showConfirmationBox();
    }, [socialAccount, addSocialAccountModalVisibility])

    useEffect(() => {
        dispatch(setShowFooterNav(false));
        return () => {
            dispatch(setShowFooterNav(true));
        }
    }, [dispatch])

    useEffect(() => {
        if(campaignData){
            const platformKey = campaignData.campaign.platform;
            const platform = platforms[platformKey as keyof typeof platforms];
            setCampaignPlatform(platform ?? null);
        }
    }, [campaignData])

    useEffect(() => {
        if(campaignPlatform){
            const eligibleAccounts = socialAccounts?.filter(account => account.social_account.platform === campaignPlatform.id);
            setEligibleSocialAccounts(eligibleAccounts ?? []);
        }
    }, [socialAccounts, campaignPlatform])

    useEffect(() => {
        if(eligibleSocialAccounts.length === 1){
            setSocialAccount(eligibleSocialAccounts[0]);
        }
    }, [eligibleSocialAccounts])

    useEffect(() => {
        if(campaignAccountSelectorVisible && socialAccount){
            setCampaignAccountSelectorVisible(false);
            showConfirmationBox();
        }
    }, [campaignAccountSelectorVisible, socialAccount])

    useEffect(() => {
        if(campaignData?.campaign){
            analyticsLog('campaign_view', {
                campaign_id: campaignData?.campaign.uuid,
                campaign_name: campaignData?.campaign.name,
                brand_name: campaignData?.campaign?.brand?.name
            })
        }
    }, [campaignData])

    useEffect(() => {
        if(socialAccounts.length > 0 && profileCompletion === 100){
            showNotificationEnablePrompt();
        }
    }, [socialAccounts, profileCompletion])

    /* Lifecycle hooks */


    /* Renders */
    if (!campaignData) return <CampaignDetailSkeleton />

    return (
        <React.Fragment>
            <div className="card card-style p-3 mb-0">
                <CampaignImageSlider images={getCampaignImages()} />
                <div className="d-flex flex-row justify-content-between align-items-center mb-2">
                    {/* <span className="chip mb-0 bg-gray-light">
                        <StrategyIcon strategy={campaignData.campaign.strategy} />
                        <strong className="color-black font-400">{textToTitleCase(campaignData.campaign.strategy)}</strong>
                    </span> */}
                    <div>
                        <img src={platformLogos[campaignPlatform?.id as keyof typeof platformLogos]} height={campaignPlatform?.id === 'ig' ? 35 : 25} />
                    </div>
                    <div>
                        <a href={'https://wa.me/?text=' + encodeURIComponent(campaignData.share_url)} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-gray-light color-black"><i className="fab fa-whatsapp"></i></a>
                        {/* <a href={'https://www.linkedin.com/shareArticle?mini=true&url=' + encodeURIComponent(campaignData.share_url) + '&title=' + campaignData.campaign.name} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-gray-light color-black"><i className="fa-brands fa-linkedin"></i></a> */}
                        <a href={'https://x.com/intent/tweet?url=' + encodeURIComponent(campaignData.share_url) + '&text=' + campaignData.campaign.name} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-gray-light color-black"><i className="fa-brands fa-x-twitter"></i></a>
                        <button className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-gray-light color-black" onClick={shareURL}><i className="fas fa-share-nodes"></i></button>
                    </div>
                </div>
                <h4 className="mt-2" style={{ fontWeight: 500 }}>{campaignData.campaign.name}</h4>
                <div>
                    {
                        campaignData.campaign.niche_names?.map((niche, index) => {
                            return (
                                <span key={index} className="chip mb-0 mt-2 me-2 ps-2 pe-2 bg-gray-light">
                                    <strong className="color-black font-400">{niche}</strong>
                                </span>
                            );
                        })
                    }
                </div>
                <div className="card shadow-none border-gray-300 mt-4" style={{ borderRadius: '1rem' }}>
                    <div className="card-body p-2">
                        <h6 className="pop fw-bold" style={{ fontWeight: 500 }}>Specifications</h6>
                        <ul className="list-group list-group-flush">
                            {
                                campaignData.brand &&
                                <li className="list-group-item px-0 d-flex justify-content-between" v-if="brand">
                                    <span className="left">Brand</span>
                                    <span className="right">{campaignData.brand.name}</span>
                                </li>
                            }
                            {/* <li className="list-group-item px-0 d-flex justify-content-between">
                                <span className="left">Strategy</span>
                                <span className="right">{campaignData.campaign.strategy_name}</span>
                            </li> */}
                            {
                                (Number(campaignData?.campaign?.budget) > 0) &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Payment</span>
                                    <span className="right text-end">
                                        upto ₹{Number(campaignData.campaign.budget)}
                                    </span>
                                </li>
                            }
                            {
                                (Number(campaignData?.campaign?.barter_budget) > 0) &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Barter/Reimbursement</span>
                                    <span className="right text-end">
                                        upto ₹{campaignData.campaign.barter_budget}
                                    </span>
                                </li>
                            }
                            {
                                campaignData.campaign?.requirements?.gender !== 'A' &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Influencer's Gender</span>
                                    <span className="right">{campaignData.campaign.requirements?.gender_name}</span>
                                </li>
                            }
                            {
                                (campaignData.campaign?.requirements?.age?.min ? true : false) &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Influencer's Age</span>
                                    <span className="right">{campaignData.campaign?.requirements?.age?.min} - {campaignData.campaign?.requirements?.age?.max}</span>
                                </li>
                            }
                            {
                                campaignData.campaign?.requirements?.regions && campaignData.campaign?.requirements?.regions.length > 0 &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Influencer Region(s)</span>
                                    <span className="right text-end">
                                        {
                                            campaignData.campaign?.requirements?.regions.map((region, i) => {
                                                return (
                                                    <div className="badge badge-light mb-1 me-1 d-block text-start" key={i}>{region}</div>
                                                )
                                            })
                                        }
                                    </span>
                                </li>
                            }
                            {
                                campaignData.campaign?.requirements?.cities && campaignData.campaign?.requirements?.cities.length > 0 &&
                                (!Array.isArray(campaignData?.campaign?.requirements?.regions) || campaignData?.campaign?.requirements?.regions.length === 0) &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Influencer Location(s)</span>
                                    <span className="right text-end">
                                        {
                                            campaignData.campaign?.requirements?.cities.map((city, i) => {
                                                return (
                                                    <div className="badge badge-light mb-1 me-1 d-block text-start" key={i}>{city}</div>
                                                )
                                            })
                                        }
                                    </span>
                                </li>
                            }
                            {
                                (campaignData.campaign?.requirements?.follower_count?.min ? true : false) &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Followers</span>
                                    <span className="right">{campaignData.campaign?.requirements?.follower_count?.min}+</span>
                                </li>
                            }
                            {
                                campaignData.campaign?.requirements?.audience_gender !== 'A' &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Audience Gender</span>
                                    <span className="right">{campaignData.campaign.requirements?.audience_gender_name}</span>
                                </li>
                            }
                            {
                                campaignData.campaign?.requirements?.audience_cities && campaignData.campaign?.requirements?.audience_cities.length > 0 &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Audience Location(s)</span>
                                    <span className="right text-end">
                                        {
                                            campaignData.campaign?.requirements?.audience_cities.map((city, i) => {
                                                return (
                                                    <div className="badge badge-light mb-1 me-1 d-block text-start" key={i}>{city}</div>
                                                )
                                            })
                                        }
                                    </span>
                                </li>
                            }
                            {
                                campaignData.campaign?.requirements?.audience_age_groups && campaignData.campaign?.requirements?.audience_age_groups.length > 0 &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Audience Age</span>
                                    <span className="right text-end">
                                        {
                                            campaignData.campaign?.requirements?.audience_age_groups.map((age, i) => {
                                                return (
                                                    <div className="badge badge-light mb-1 me-1 text-start" key={i}>{age}</div>
                                                )
                                            })
                                        }
                                    </span>
                                </li>
                            }
                            {
                                campaignData.campaign?.requirements?.languages && campaignData.campaign?.requirements?.languages.length > 0 &&
                                <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Languages</span>
                                    <span className="right text-end">
                                        {
                                            campaignData.campaign?.requirements?.languages.map((languageId, i) => {
                                                return (
                                                    <div className="badge badge-light mb-1 me-1 text-start" key={i}>{languages[languageId]}</div>
                                                )
                                            })
                                        }
                                    </span>
                                </li>
                            }
                        </ul>
                    </div>
                </div>

                <div className={`col-12 mt-0`}>
                    {
                        campaignData?.campaign?.description &&
                        <div>
                            <h4 className="pop">Description</h4>
                            <div style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sanitizeURLs(campaignData?.campaign?.description ?? "")) }}></div>
                        </div>
                    }
                    {
                        Array.isArray(campaignData?.milestones) && campaignData.milestones.length > 0 &&
                        <div className="specification-card p-0 my-4">
                            <div className="specification-card-header">
                                <h6 className="specification-card-title pop">Media Requirements</h6>
                                {/* <img src={MilestoneMediaPNG} className='milestone-spec-illustrator' alt='milestone-payout' /> */}
                            </div>
                            {
                                [...campaignData.milestones].sort((a, b) => a.sort_order - b.sort_order).map((milestone, i) => (
                                    <div className='specification-card-content' key={i}>
                                        {
                                            milestone.gig_type.slug !== 'general' &&
                                            <h6 className="m-0 mt-2" style={{ fontWeight: 500 }}>{(campaignData.milestones ?? []).length > 1 ? `#${milestone.sort_order} ` : ''}{milestone.gig_type.name}</h6>
                                        }
                                        <div className="milestone-media-requirements">
                                            {
                                                campaignData?.campaign.platform === 'yt' &&
                                                <div className="milestone-media-requirement">
                                                    <span>{milestone.content_media_types.VIDEO}</span>
                                                    <span>Video</span>
                                                </div>
                                            }
                                            {
                                                campaignData?.campaign.platform === 'ig' &&
                                                <>
                                                    <div className="milestone-media-requirement">
                                                        <span>{milestone.content_media_types.REELS}</span>
                                                        <span>Reel</span>
                                                    </div>
                                                    <div className="milestone-media-requirement">
                                                        <span>{milestone.content_media_types.STORY}</span>
                                                        <span>Story</span>
                                                    </div>
                                                    <div className="milestone-media-requirement">
                                                        <span>{milestone.content_media_types.FEED}</span>
                                                        <span>Post</span>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                        <div className="milestone-media-requirements">
                                            {
                                                milestone.payment_days &&
                                                <div className="milestone-media-requirement">
                                                    <span>{milestone.payment_days} <span className="subtext">day{milestone.payment_days > 1 ? 's' : ''}</span></span>
                                                    <span>Payment</span>
                                                </div>
                                            }
                                            {
                                                milestone?.specifications?.content_rights_duration &&
                                                <div className="milestone-media-requirement">
                                                    <span>{milestone.specifications.content_rights_duration} <span className='subtext'>month{Number(milestone.specifications.content_rights_duration) > 1 ? 's' : ''}</span></span>
                                                    <span>Content Rights</span>
                                                </div>
                                            }
                                            {
                                                milestone?.specifications?.ad_rights_duration &&
                                                <div className="milestone-media-requirement">
                                                    <span>{milestone.specifications.ad_rights_duration} <span className='subtext'>month{Number(milestone.specifications.ad_rights_duration) > 1 ? 's' : ''}</span></span>
                                                    <span>Ad Rights</span>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                ))
                            }

                        </div>
                    }
                    {
                        campaignData.brand && campaignData.brand.description &&
                        <div className="mt-2">
                            <h4 className="pop">About the Brand</h4>
                            {
                                campaignData.brand.website_url &&
                                <a href={campaignData.brand.website_url} target="_blank" rel="noopener noreferrer">{campaignData.brand.website_url}</a>
                            }
                            <p style={{ whiteSpace: 'pre-wrap' }}>{campaignData.brand.description}</p>
                        </div>
                    }
                </div>
            </div>
            <div className="sticky-footer flex-column">
                {/* {
                    ((locationDeclined || !isLocationServiceEnabled) && locationRequired && false) && (
                      <div className="my-2 mt-0 w-100 text-center">
                        <span className="text-danger">
                          Allow access to your location to apply.

                          <div className="d-flex align-items-center justify-content-center gap-2 w-100 text-primary">
                            
                            {isFetchingLocation ? (
                              <button className="unset-all text-muted cursor-default" type="button" disabled>
                                <i className="fa-solid fa-spinner fa-spin"></i> Fetching location…
                              </button>
                            ) : (
                              <button
                                className="unset-all text-primary cursor-pointer"
                                type="button"
                                onClick={() => {
                                  if (isNativePlatform()) {
                                    allowLocationAccess();
                                  } else {
                                    requestLocation();
                                  }
                                }}
                              >
                                <i className="fa-solid fa-location-arrow"></i> Allow
                                {
                                    !isNativePlatform() &&
                                    <>
                                        <Link to={`/help/location?browser=${encodeURIComponent(detectBrowser())}`} className="text-primary" target="_blank">
                                            {' '}| <i className="fa-solid fa-circle-info me-1"></i>
                                            View Help
                                        </Link>
                                    </>
                                }
                              </button>
                            )}

                          </div>
                        </span>
                      </div>
                    )
                } */}
                {
                    applicationStatus.status !== 'APPLIED' && campaignData?.campaign.status === 'completed' &&
                    <button className="w-100 create-earning-link-btn" disabled={true}>Applications Closed</button>
                }
                {
                    canApply && applicationStatus.status !== 'APPLIED' && (!applicationError) && campaignData?.campaign.status !== 'completed' &&
                    // <button className="w-100 create-earning-link-btn w-100" disabled={applicationStatus.status === 'PENDING' || ((locationDeclined || !isLocationServiceEnabled) && locationRequired && false)} onClick={showConfirmationBox}>{applicationStatus.status === 'PENDING' ? 'Applying..' : 'Apply'}</button>
                    <button className="w-100 create-earning-link-btn w-100" disabled={applicationStatus.status === 'PENDING'} onClick={showConfirmationBox}>{applicationStatus.status === 'PENDING' ? 'Applying..' : 'Apply'}</button>
                }
                {
                    applicationStatus.status === 'APPLIED' && (!applicationError) &&
                    <Link to={{pathname: ((applicationData?.application ?? '') as string) === 'accepted' ? '/collaborations' : "/applications"}} className="w-100 create-earning-link-btn w-100">View Status</Link>
                }
            </div>

            <InfluencerActionCardModal
                isActive={connectionRequiredModalVisibility}
                onClose={() => {
                    setConnectionRequiredModalVisibility(false);
                    if(eligibleSocialAccounts.length > 1){
                        setSocialAccount(null);
                    }
                }}
                socialAccount={socialAccount?.social_account}
            />
            <CampaignAccountSelectorModal
                isActive={campaignAccountSelectorVisible}
                onClose={() => setCampaignAccountSelectorVisible(false)}
                socialAccounts={eligibleSocialAccounts}
                onAccountSelection={(account) => {
                    setSocialAccount(account);
                }}
            />
            {
                canApply && socialAccount &&
                <React.Fragment>
                    <CampaignApplyConfirmationModal
                        isActive={confirmationBox}
                        onClose={() => {
                            setConfirmationBox(false)
                            if(eligibleSocialAccounts.length > 1){
                                setSocialAccount(null);
                            }
                        }}
                        application={applicationData!}
                        apply={apply}
                        socialAccount={socialAccount?.social_account!}
                        status={applicationStatus.status}
                        campaignRequirements={campaignData.campaign.requirements}
                    />
                    <OutOfCredits 
                        isActive={(applicationError && applicationError.error_code === ERROR.MONTHLY_APPLICATIONS_EXHAUSTED) ? true : false}
                        onClose={() => setApplicationError(null)}
                    />
                    <InsufficientPermissionsCTAModal
                        isActive={(applicationError && applicationError.error_code === ERROR.INSUFFICIENT_PERMISSIONS) ? true : false}
                        onClose={() => setApplicationError(null)}
                        missing_permissions={applicationError?.missing_permissions!}
                        feature={applicationError?.feature!}
                        page="campaign"
                    />
                    <GenericErrorsCTAModal
                        isActive={(applicationError && ![ERROR.INSUFFICIENT_PERMISSIONS, ERROR.MONTHLY_APPLICATIONS_EXHAUSTED].includes(applicationError.error_code)) ? true : false}
                        onClose={() => setApplicationError(null)}
                        error={applicationError!}
                    />
                </React.Fragment>

            }
        </React.Fragment>

    );
}

export default Campaign;